/*
  Infinite scroll for the product grid on collection and search pages.

  Forward loading
  - the next page is requested through the Section Rendering API
    (?section_id=...), so only this section comes back, not the whole page;
  - loading starts OFFSET_SCREENS screens before the end of the grid
    (IntersectionObserver on the pagination). The next page is NOT
    prefetched: see the note in loadMore.

  Backward loading
  - when the grid does not start from page 1 (a direct ?page=N link, a reload)
    the "Show previous products" control is rendered above it
    (snippets/infinite-scroll-previous.liquid);
  - scrolling up towards it loads the previous page above the grid and keeps
    the cards on screen in place; a click on it loads the previous page right
    under it; without JS it is a plain link.

  The address is never rewritten and scroll restoration is never touched.

  That is deliberate. Returning from a product page is left entirely to the
  browser: Safari and Chrome keep the page in the back/forward cache and hand
  it back with its DOM and its scroll position. A theme that writes ?page=N
  into the address or sets history.scrollRestoration = 'manual' has to rebuild
  that state by hand, and gets it wrong as soon as the server renders a single
  page - which is exactly what the reference implementation (that does nothing
  here) avoids.

  Markup contract (sections with the "Infinite scroll" setting on):
    #ProductGridContainer[data-infinite-scroll]
      [data-infinite-prev] > a[data-infinite-prev-link]   (page > 1 only)
      .product-grid                                       the cards
      [data-infinite-pagination][data-loading-text]
        li.infinite_next[data-infinite-next] > a[href]    next page

  After every insert the card helpers of the theme run for the new cards
  (card sliders, colour swatches, reveal-on-scroll animation) and
  "ajaxinate:loaded" is fired on document with { items, url, direction }.
*/
(function () {
  'use strict';

  if (window.Ajaxinate) return;

  var ROOT = '#ProductGridContainer';
  var ENABLED_ROOT = ROOT + '[data-infinite-scroll]';
  var LIST = '.product-grid';
  var PAGINATION = '[data-infinite-pagination]';
  var NEXT_LINK = '[data-infinite-next] a[href]';
  var PREV = '[data-infinite-prev]';
  var PREV_LINK = '[data-infinite-prev-link]';
  var OFFSET_SCREENS = 2.5;
  var PREV_OFFSET_SCREENS = 1.5;
  // Safety net for the scroll position only: iOS Safari sometimes hands the
  // page back from its cache with the DOM intact but at the top. Nothing here
  // touches the address or history.scrollRestoration.
  var SCROLL_KEY = 'ajaxinate:scroll';
  var SCROLL_SAVE_MS = 500;
  var lastScrollSave = 0;

  var designMode = Boolean(window.Shopify && window.Shopify.designMode);
  var instances = new WeakMap();
  var live = new Set();

  /* ---------------------------------------------------------------- helpers */

  function pageFromUrl() {
    return parseInt(new URL(window.location.href).searchParams.get('page'), 10) || 1;
  }

  function isPlainClick(event) {
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0);
  }

  /* ------------------------------------------------------------ diagnostics */

  // On-device diagnostics: there is no devtools on a phone. Silent until asked
  // for with ?ajaxinate=debug, sessionStorage 'ajaxinate-debug' = '1', or
  // window.__ajaxinateWatch(). The panel has a Copy button, because selecting
  // text on a phone is hopeless.
  var LOG_LIMIT = 120;
  var logLines = [];
  var logPanel = null;
  var logText = null;
  var logCopyButton = null;

  function loggingEnabled() {
    try {
      if (window.__ajaxinateDebug) return true;
      if (window.location.search.indexOf('ajaxinate=debug') !== -1) return true;
      return sessionStorage.getItem('ajaxinate-debug') === '1';
    } catch (error) {
      return false;
    }
  }

  function log(message) {
    if (!loggingEnabled()) return;
    logLines.push({
      t: Math.round(window.performance && performance.now ? performance.now() : Date.now()),
      m: message,
    });
    if (logLines.length > LOG_LIMIT) logLines.shift();
    showLogPanel();
  }

  function logState() {
    return 'y=' + Math.round(window.pageYOffset) +
      ' ih=' + window.innerHeight +
      ' doc=' + (document.documentElement.scrollHeight || 0) +
      ' page=' + pageFromUrl() +
      ' vv=' + (history.scrollRestoration || 'n/a');
  }

  function showLogPanel() {
    if (!document.body) return;

    if (!logPanel) {
      logPanel = document.createElement('div');
      logPanel.id = 'ajaxinate-debug-log';
      logPanel.style.cssText =
        'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;max-height:55vh;' +
        'display:flex;flex-direction:column;background:rgba(0,0,0,.9);' +
        'font:11px/1.35 ui-monospace,Menlo,monospace;color:#8f8';

      var head = document.createElement('div');
      head.style.cssText =
        'display:flex;align-items:center;gap:8px;padding:6px 8px;' +
        'border-bottom:1px solid rgba(255,255,255,.15);flex:0 0 auto';

      var title = document.createElement('span');
      title.textContent = 'ajaxinate debug';

      logCopyButton = document.createElement('button');
      logCopyButton.type = 'button';
      logCopyButton.textContent = 'Copy';
      logCopyButton.style.cssText =
        'margin-left:auto;padding:6px 12px;border:1px solid #8f8;border-radius:6px;' +
        'background:transparent;color:#8f8;font:inherit;font-weight:600';
      logCopyButton.addEventListener('click', function (event) {
        event.preventDefault();
        copyLog();
      });

      head.appendChild(title);
      head.appendChild(logCopyButton);

      logText = document.createElement('pre');
      logText.style.cssText =
        'margin:0;padding:8px 10px;overflow:auto;flex:1 1 auto;' +
        'white-space:pre-wrap;word-break:break-word;-webkit-user-select:text;user-select:text';

      logPanel.appendChild(head);
      logPanel.appendChild(logText);
      document.body.appendChild(logPanel);
    }

    var snap = 'snapshot: ' + logState() +
      '\n  url=' + window.location.pathname + window.location.search +
      '\n  items=' + document.querySelectorAll('#product-grid > *').length +
      '\n  elements: container=' + document.querySelectorAll(ENABLED_ROOT).length +
      ' list=' + document.querySelectorAll(ROOT + ' ' + LIST).length +
      ' pagination=' + document.querySelectorAll(ROOT + ' ' + PAGINATION).length +
      ' prev=' + document.querySelectorAll(ROOT + ' ' + PREV).length +
      '\n  navType=' + navigationType() +
      ' saved=' + storedText();
    logText.textContent = snap + '\n' + logLines.map(function (line) {
      return line.t + 'ms  ' + line.m;
    }).join('\n');
  }

  function copyLog() {
    var text = logText ? logText.textContent : '';
    if (!text) return;

    function done(ok) {
      if (!logCopyButton) return;
      logCopyButton.textContent = ok ? 'Copied' : 'Select all';
      setTimeout(function () {
        if (logCopyButton) logCopyButton.textContent = 'Copy';
      }, 1500);
      if (!ok && logText) {
        try {
          var range = document.createRange();
          range.selectNodeContents(logText);
          var selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (error) {
          /* nothing else to offer */
        }
      }
    }

    if (window.navigator.clipboard && window.navigator.clipboard.writeText) {
      window.navigator.clipboard.writeText(text).then(function () {
        done(true);
      }).catch(function () {
        done(legacyCopy(text));
      });
      return;
    }
    done(legacyCopy(text));
  }

  function legacyCopy(text) {
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', 'readonly');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
    document.body.appendChild(area);
    var ok = false;
    try {
      area.select();
      area.setSelectionRange(0, text.length);
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }
    document.body.removeChild(area);
    return ok;
  }

  window.__ajaxinateLog = function () {
    var current = logLines.map(function (line) {
      return line.t + 'ms  ' + line.m;
    }).join('\n');
    console.log('--- this page load ---\n' + current);
    return { current: logLines };
  };

  window.__ajaxinateWatch = function () {
    window.__ajaxinateDebug = true;
    log('watch enabled ' + logState());
    return 'ajaxinate debug watch on';
  };

  /* --------------------------------------------------------------- instance */

  function Ajaxinate(root) {
    this.root = root;
    this.list = root.querySelector(LIST);
    this.pagination = root.querySelector(PAGINATION);
    this.prev = root.querySelector(PREV);
    var grid = root.querySelector('#product-grid');
    this.sectionId = grid ? grid.dataset.id : '';
    this.cache = new Map();
    this.controller = window.AbortController ? new AbortController() : null;
    this.loadingNext = false;
    this.loadingPrev = false;
    this.destroyed = false;

    this.onIntersect = this.onIntersect.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);

    log('init: list=' + Boolean(this.list) + ' pagination=' + Boolean(this.pagination) +
      ' prev=' + Boolean(this.prev) + ' section=' + this.sectionId +
      ' navType=' + navigationType() + ' ' + logState());

    if (!this.list) return;

    if (this.pagination) this.watch();
    var prevLink = this.prevLink();
    if (prevLink) prevLink.addEventListener('click', this.onPrevClick);
    live.add(this);
  }

  Ajaxinate.prototype.nextLink = function () {
    return this.pagination ? this.pagination.querySelector(NEXT_LINK) : null;
  };

  Ajaxinate.prototype.prevLink = function () {
    return this.prev ? this.prev.querySelector(PREV_LINK) : null;
  };

  // Watch the current pagination: scroll trigger + click on the link
  Ajaxinate.prototype.watch = function () {
    var link = this.nextLink();
    if (!link) return;

    link.addEventListener('click', this.onNextClick);

    if (!('IntersectionObserver' in window)) return;
    var offset = Math.round(window.innerHeight * OFFSET_SCREENS);
    this.observer = new IntersectionObserver(this.onIntersect, {
      rootMargin: offset + 'px 0px',
    });
    this.observer.observe(this.pagination);
  };

  Ajaxinate.prototype.unwatch = function () {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };

  Ajaxinate.prototype.onIntersect = function (entries) {
    var visible = entries.some(function (entry) {
      return entry.isIntersecting;
    });
    if (visible) this.loadMore();
  };

  Ajaxinate.prototype.onNextClick = function (event) {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    this.loadMore();
  };

  Ajaxinate.prototype.onPrevClick = function (event) {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    // The control is on screen: the previous products open right under it
    this.loadPrevious(false);
  };

  Ajaxinate.prototype.sectionUrl = function (url) {
    var target = new URL(url, window.location.origin);
    if (this.sectionId) target.searchParams.set('section_id', this.sectionId);
    return target.toString();
  };

  // One request per page: repeated calls for the same url share the promise
  Ajaxinate.prototype.fetchPage = function (url) {
    if (this.cache.has(url)) return this.cache.get(url);

    var self = this;
    var options = this.controller ? { signal: this.controller.signal } : {};
    var request = fetch(this.sectionUrl(url), options).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.text();
    });
    this.cache.set(url, request);
    // A failed request is forgotten, so a click can try again
    request.catch(function () {
      self.cache.delete(url);
    });
    return request;
  };

  // Cards of a fetched page, ready to insert
  Ajaxinate.prototype.parsePage = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newList = doc.querySelector(ROOT + ' ' + LIST);
    if (!newList) throw new Error('No product grid in the response');

    var items = Array.prototype.map.call(newList.children, function (node) {
      return document.importNode(node, true);
    });
    var fragment = document.createDocumentFragment();
    items.forEach(function (item) {
      fragment.appendChild(item);
    });
    return { doc: doc, items: items, fragment: fragment };
  };

  function setLinkLoading(container, link, isLoading, loadingText) {
    if (!container || !link) return;
    container.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      link.dataset.label = link.innerHTML;
      link.setAttribute('aria-busy', 'true');
      link.textContent = loadingText || '...';
    } else {
      if (link.dataset.label !== undefined) link.innerHTML = link.dataset.label;
      link.removeAttribute('aria-busy');
    }
  }

  Ajaxinate.prototype.loadingText = function () {
    return (this.pagination && this.pagination.dataset.loadingText) ||
      (this.prev && this.prev.dataset.loadingText) || '...';
  };

  /* ------------------------------------------------------- forward loading */

  Ajaxinate.prototype.loadMore = function (onDone) {
    var link = this.nextLink();
    if (this.loadingNext || this.destroyed || !link) return;

    var self = this;
    var url = link.href;
    this.loadingNext = true;
    this.unwatch();
    setLinkLoading(this.pagination, link, true, this.loadingText());
    log('loadMore: fetching ' + url);

    this.fetchPage(url)
      .then(function (html) {
        if (self.destroyed) return;
        self.cache.delete(url);

        var result = self.parsePage(html);
        self.list.appendChild(result.fragment);

        var newPagination = result.doc.querySelector(ROOT + ' ' + PAGINATION);
        var hasNext = newPagination && newPagination.querySelector(NEXT_LINK);
        if (hasNext) {
          var adopted = document.importNode(newPagination, true);
          self.pagination.replaceWith(adopted);
          self.pagination = adopted;
          self.watch();
          // No prefetch on purpose: an in-flight request when the shopper
          // leaves can keep the page out of Safari's page cache. The next page
          // is loaded when it is actually needed.
        } else {
          // Last page: nothing more to load
          self.pagination.remove();
          self.pagination = null;
        }

        log('loadMore: inserted ' + result.items.length + ' cards, next=' + Boolean(hasNext));
        afterInsert(self.list, result.items, url, 'next');
      })
      .catch(function (error) {
        if (self.destroyed || (error && error.name === 'AbortError')) return;
        console.error('Ajaxinate: failed to load the next page', error);
        // Stop auto loading; the link goes back to a normal page link
        if (link.isConnected) {
          setLinkLoading(self.pagination, link, false);
          link.removeEventListener('click', self.onNextClick);
        }
      })
      .then(function () {
        self.loadingNext = false;
        if (typeof onDone === 'function' && !self.destroyed) onDone();
      });
  };

  /* ------------------------------------------------------ backward loading */

  // keepPosition: the cards on screen stay where they are (scrolling up);
  // otherwise the new cards push the grid down
  Ajaxinate.prototype.loadPrevious = function (keepPosition) {
    var link = this.prevLink();
    if (this.loadingPrev || this.destroyed || !link) return;

    var self = this;
    var url = link.href;
    this.loadingPrev = true;
    setLinkLoading(this.prev, link, true, this.loadingText());
    log('loadPrevious: fetching ' + url);

    this.fetchPage(url)
      .then(function (html) {
        if (self.destroyed) return;
        self.cache.delete(url);

        var result = self.parsePage(html);
        var reference = self.list.firstElementChild;
        var before = reference ? reference.getBoundingClientRect().top : 0;

        self.list.insertBefore(result.fragment, self.list.firstChild);

        // Where the browser supports scroll anchoring it has already kept
        // the reference card in place and the difference is 0
        if (keepPosition && reference) {
          var delta = reference.getBoundingClientRect().top - before;
          if (Math.abs(delta) > 1) window.scrollTo(0, window.pageYOffset + delta);
        }

        // The control keeps pointing at the page before the new first one, or
        // disappears once page 1 is back
        var newPrev = result.doc.querySelector(ROOT + ' ' + PREV);
        if (newPrev) {
          var adopted = document.importNode(newPrev, true);
          self.prev.replaceWith(adopted);
          self.prev = adopted;
          var nextPrevLink = self.prevLink();
          if (nextPrevLink) nextPrevLink.addEventListener('click', self.onPrevClick);
        } else if (self.prev) {
          self.prev.remove();
          self.prev = null;
        }

        log('loadPrevious: inserted ' + result.items.length + ' cards, prev=' +
          Boolean(self.prev));
        afterInsert(self.list, result.items, url, 'previous');
      })
      .catch(function (error) {
        if (self.destroyed || (error && error.name === 'AbortError')) return;
        console.error('Ajaxinate: failed to load the previous page', error);
        if (link.isConnected) {
          setLinkLoading(self.prev, link, false);
          link.removeEventListener('click', self.onPrevClick);
          self.prevFailed = true;
        }
      })
      .then(function () {
        self.loadingPrev = false;
      });
  };

  // Scrolling up with the control near or on screen loads the previous page
  Ajaxinate.prototype.checkPrevious = function (goingUp) {
    if (!goingUp || !this.prev || this.prevFailed || this.loadingPrev) return;
    var rect = this.prev.getBoundingClientRect();
    var margin = window.innerHeight * PREV_OFFSET_SCREENS;
    if (rect.bottom > -margin && rect.top < window.innerHeight) this.loadPrevious(true);
  };

  // Chrome on Android does not always return the collection from its cache:
  // it reloads it, so the grid comes back as page 1 and the offset is lost.
  // Rebuild the pages the shopper had and put the offset back.
  Ajaxinate.prototype.rebuildTo = function (saved) {
    var self = this;
    var target = saved.items || 0;
    var attempts = 0;

    function step() {
      if (self.destroyed) return;
      if (self.list.children.length >= target || !self.nextLink() || attempts++ >= 20) {
        return done();
      }
      self.loadMore(step);
    }

    function done() {
      window.scrollTo(0, saved.y);
      requestAnimationFrame(function () {
        if (window.pageYOffset < saved.y - 2) window.scrollTo(0, saved.y);
      });
      log('reload return: ' + self.list.children.length + ' items, restored y=' + saved.y);
    }

    step();
  };

  Ajaxinate.prototype.destroy = function () {
    this.destroyed = true;
    live.delete(this);
    this.unwatch();
    var next = this.nextLink();
    if (next) next.removeEventListener('click', this.onNextClick);
    var prev = this.prevLink();
    if (prev) prev.removeEventListener('click', this.onPrevClick);
    if (this.controller) this.controller.abort();
    this.cache.clear();
  };

  /* ---------------------------------------------------------- shared hooks */

  // Theme helpers that normally run once on page load, repeated for new cards
  function afterInsert(list, items, url, direction) {
    // Card sliders, swatch galleries and the mobile swatch sync (pm-custom.js):
    // both entry points skip cards they have already initialised
    if (typeof window.initProductCardSliders === 'function') {
      window.initProductCardSliders(list);
    }
    if (typeof window.initProductMediaGalleries === 'function') {
      window.initProductMediaGalleries(list);
    }
    if (typeof window.initColorSwatches === 'function') {
      window.initColorSwatches(list);
    }

    // Reveal-on-scroll (assets/animations.js). The new cards carry
    // .scroll-trigger.animate--slide-in, which base.css paints at
    // opacity: 0.01 until the class .scroll-trigger--offscreen is lifted by
    // the observer - without this they would be inserted invisible.
    if (typeof window.initializeScrollAnimationTrigger === 'function') {
      var animated = items.some(function (item) {
        return item.classList && item.classList.contains('scroll-trigger');
      });
      if (animated) window.initializeScrollAnimationTrigger(list);
    }

    document.dispatchEvent(
      new CustomEvent('ajaxinate:loaded', { detail: { items: items, url: url, direction: direction } })
    );
  }

  var lastScrollY = window.pageYOffset;
  var scrollQueued = false;

  function cardCount() {
    var count = 0;
    live.forEach(function (instance) {
      if (instance.list) count = Math.max(count, instance.list.children.length);
    });
    return count;
  }

  function storedText() {
    try {
      return sessionStorage.getItem(SCROLL_KEY) || 'none';
    } catch (error) {
      return 'blocked';
    }
  }

  function navigationType() {
    var entries = performance.getEntriesByType ? performance.getEntriesByType('navigation') : [];
    if (entries && entries[0]) return entries[0].type;
    if (performance.navigation) {
      return { 1: 'reload', 2: 'back_forward' }[performance.navigation.type] || 'navigate';
    }
    return 'navigate';
  }

  function saveScroll(y, items) {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({
        y: Math.round(y),
        items: items || 0,
        t: Date.now(),
      }));
    } catch (error) {
      /* storage blocked: the browser's own restore is still there */
    }
  }

  function onScrollFrame() {
    scrollQueued = false;
    var y = window.pageYOffset;
    var goingUp = y < lastScrollY;
    lastScrollY = y;
    live.forEach(function (instance) {
      instance.checkPrevious(goingUp);
    });

    // Keep a fresh offset while the shopper scrolls: on iOS the page can go
    // into the cache without a usable pagehide, and then there is nothing to
    // put back.
    var now = Date.now();
    if (now - lastScrollSave > SCROLL_SAVE_MS) {
      lastScrollSave = now;
      saveScroll(y, cardCount());
    }
  }

  window.addEventListener(
    'scroll',
    function () {
      if (scrollQueued) return;
      scrollQueued = true;
      requestAnimationFrame(onScrollFrame);
    },
    { passive: true }
  );

  window.addEventListener('pagehide', function (event) {
    saveScroll(window.pageYOffset, cardCount());
    log('pagehide persisted=' + Boolean(event && event.persisted) + ' ' + logState() +
      ' items=' + document.querySelectorAll('#product-grid > *').length);
  });

  window.addEventListener('pageshow', function (event) {
    var persisted = Boolean(event && event.persisted);
    log('pageshow persisted=' + persisted + ' ' + logState() +
      ' items=' + document.querySelectorAll('#product-grid > *').length);

    // Only when the browser handed the page back from its cache and left it
    // at the top: put the offset the shopper left at back. If the browser
    // restored the offset itself, this does nothing.
    if (!persisted || window.pageYOffset > 0) return;
    var saved = 0;
    try {
      saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || '{}');
    } catch (error) {
      return;
    }
    if (!saved || !(saved.y > 0)) return;
    if (Date.now() - (saved.t || 0) > 60000) return;

    var restore = function () {
      if (window.pageYOffset > 0) return;
      window.scrollTo(0, saved.y);
      log('pageshow: restored y=' + saved.y + ' after the cache return');
    };
    restore();
    requestAnimationFrame(restore);
  });

  // A reload-like return: Chrome on Android rebuilt the page instead of taking
  // it from the cache. The offset and the number of cards are in storage; rebuild
  // to that depth and put the offset back. A normal first visit never gets here.
  var reloadRestoreAttempted = false;

  function restoreAfterReload() {
    if (reloadRestoreAttempted || designMode) return;
    reloadRestoreAttempted = true;

    // Chrome reports a back navigation that had to be rebuilt as back_forward,
    // but some builds report it as reload. A fresh visit is navigate and is
    // left alone.
    var type = navigationType();
    var saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || '{}');
    } catch (error) {
      saved = null;
    }

    log('return check: navType=' + type + ' y=' + Math.round(window.pageYOffset) +
      ' saved=' + storedText());

    if ((type !== 'back_forward' && type !== 'reload') || window.pageYOffset > 0) return;
    if (!saved || !(saved.y > 0) || Date.now() - (saved.t || 0) > 30000) return;

    var handled = false;
    live.forEach(function (instance) {
      if (handled || !instance.list) return;
      handled = true;
      if (instance.list.children.length >= (saved.items || 0)) {
        window.scrollTo(0, saved.y);
        log('reload return: restored y=' + saved.y);
        return;
      }
      instance.rebuildTo(saved);
    });
  }

  function setup(root) {
    var current = instances.get(root);
    if (current) current.destroy();
    var instance = new Ajaxinate(root);
    instances.set(root, instance);
    return instance;
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll(ENABLED_ROOT);
    Array.prototype.forEach.call(roots, function (root) {
      setup(root);

      if (root.dataset.ajaxinateObserved) return;
      root.dataset.ajaxinateObserved = 'true';
      // facets.js replaces the grid on filter, sort and history changes: rebuild
      // for the new list. Our own inserts and the pagination swap must not count,
      // so compare the list node itself instead of watching every mutation.
      var observedList = root.querySelector(LIST);
      new MutationObserver(function () {
        var currentList = root.querySelector(LIST);
        if (currentList === observedList) return;
        observedList = currentList;
        setup(root);
      }).observe(root, { childList: true, subtree: true });
    });

    // Right after the first init, before load waits for images and apps
    if (!reloadRestoreAttempted) setTimeout(restoreAfterReload, 80);
  }

  window.Ajaxinate = Ajaxinate;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: the section is re-rendered after a settings change
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var roots = event.target.querySelectorAll(ROOT);
    Array.prototype.forEach.call(roots, function (root) {
      var current = instances.get(root);
      if (current) current.destroy();
      instances.delete(root);
    });
  });
})();
